<template>
    <div class="focus-table">
        <div class="display-flex mb-lg">
            <AonButton
                class="table-button"
                :class="{ 'active-table-button': activeSideBarType === 'filters' }"
                label="Filters"
                text-with-icon
                type="subtle"
                :icon-options="{ iconStyle: 'fas', iconName: 'fa-bars-filter' }"
                @clicked="() => toggleSideBar('filters', gridApi)"
            />
            <AonButton
                class="ml-md table-button"
                :class="{ 'active-table-button': activeSideBarType === 'columns' }"
                label="Columns"
                text-with-icon
                type="subtle"
                :icon-options="{ iconStyle: 'fas', iconName: 'fa-objects-column' }"
                @clicked="() => toggleSideBar('columns', gridApi)"
            />
            <div
                class="clear-filters display-flex align-items-center cursor-pointer p-sm ml-sm"
                @click="clearFilters"
            >
                <font-awesome-icon :icon="`fas fa-xmark`" class="text-moat-600" />
                <p class="geist-font text-moat-600 bold ml-xs">Clear Filters</p>
            </div>
        </div>
        <MoatTable
            style="width: 100%; height: 89%"
            :loading="props.loading"
            :class="themeClass"
            :column-defs="colDefs"
            :row-data="props.tableData"
            :sorting-order="['desc', 'asc', null]"
            :side-bar="sideBar"
            :tooltip-show-delay="500"
            :is-external-filter-present="shouldApplyQuickFilter"
            :does-external-filter-pass="applyQuickFilter"
            :overlay-no-rows-template="noRowsTemplate"
            @filter-changed="onFilterSortChanged"
            @sort-changed="onFilterSortChanged"
            @row-data-updated="onRowDataUpdated"
            @grid-ready="onGridReady"
        />
    </div>
</template>

<script setup>
import { ref, inject, watch, nextTick, computed } from 'vue'
import { AgGridVue as MoatTable } from '@ag-grid-community/vue3'
import { formatTpeRange } from '@/lib/financial.js'
import { getInvestmentGroup } from '@/lib/investments'
import { useI18n } from 'vue-i18n'
import { useFlag } from '@unleash/proxy-client-vue'
import { usePatentViewerStore } from '@/stores'
import { applyQuickFilter as benchmarkingFilter } from '@/lib/benchmarking'
import { useAgGridSidebar } from '@/composables/useAgGridSidebar.js'

const { t } = useI18n()

const filters = inject('filters')
const props = defineProps({
    tableData: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
    quickFilter: {
        type: String,
        default: null,
    },
    techAreaIds: {
        type: Array,
        default: () => [],
    },
    allowActions: {
        type: Boolean,
        default: true,
    },
    noRowsText: {
        type: String,
        default: 'No rows',
    },
    patentListType: {
        type: String,
        default: null,
    },
    includeOptionalColumns: {
        type: Array,
        default: () => [],
    },
})
const noRowsTemplate = computed(() => {
    return `<span class="ag-overlay-no-rows-text">${props.noRowsText}</span>`
})

const emit = defineEmits(['filter-change'])

const {
    sideBar,
    activeSideBarType,
    toggleSideBar,
    injectSidebarHeaders,
    clearFilters: sidebarClearFilters,
} = useAgGridSidebar({
    panels: [
        {
            id: 'columns',
            label: 'Columns',
            width: 275,
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivotMode: true,
            },
        },
        {
            id: 'filters',
            label: 'Filters',
            width: 275,
            toolPanel: 'agFiltersToolPanel',
        },
    ],
    injectHeaders: {
        filters: 'Filters',
        columns: 'Columns',
    },
    position: 'left',
})
const gridApi = ref(null)
const themeClass = ref('ag-theme-quartz')
const displayedRowCount = ref(0)
const isProUser = useFlag('ipAlpha.proUsers')
const isArcUser = useFlag('ipAlpha.archUsersOnly')

const patentViewerStore = usePatentViewerStore()

const onGridReady = (params) => {
    gridApi.value = params.api
    injectSidebarHeaders()
    updateRowCount()
}

const onRowDataUpdated = () => {
    setTimeout(updateRowCount, 0)
}

const updateRowCount = () => {
    if (!gridApi.value) return

    const count = gridApi.value.getDisplayedRowCount()
    displayedRowCount.value = count
}

defineExpose({
    displayedRowCount,
})

watch(
    () => props.quickFilter,
    (newVal, oldVal) => {
        if (newVal !== oldVal && gridApi.value) {
            gridApi.value.onFilterChanged()
            setTimeout(updateRowCount, 0)
        }
    },
    { immediate: false }
)

watch(
    () => props.tableData,
    () => {
        setTimeout(updateRowCount, 0)
    },
    { immediate: false, deep: true }
)

const colDefs = computed(() => [
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('productAlignment.definitionTooltips.companyName'),
            headerName: 'Company Name',
            showFilterButton: true,
        },
        cellRenderer: 'MTCustomCellCompany',
        cellRendererParams: {
            allowNewTab: true,
            tabOnLeft: true,
        },
        field: 'aonEntityName',
        headerName: 'Company Name',
        headerTooltip: 'Company Name',
        minWidth: 300,
        flex: 1,
        sortingOrder: ['asc', 'desc', null],
        filter: 'agSetColumnFilter',
        pinned: 'left',
        lockPosition: true,
        chartDataType: 'category',
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            headerName: 'Ultimate Parent Company',
            showFilterButton: true,
        },
        cellRenderer: 'MTCustomCellCompany',
        cellRendererParams: {
            allowNewTab: true,
            tabOnLeft: true,
        },
        field: 'ultimateAonEntityName',
        headerName: 'Ultimate Parent Name',
        headerTooltip: 'Ultimate Parent Name',
        minWidth: 300,
        flex: 1,
        sortingOrder: ['asc', 'desc', null],
        filter: 'agTextColumnFilter',
        pinned: 'left',
        hide: true,
        chartDataType: 'category',
    },
    {
        field: 'competitorRank',
        headerName: 'Rank',
        valueFormatter: (p) => {
            if (p.data) {
                return `${p.data.competitorRank.toLocaleString()}`
            }
        },
        headerTooltip: 'Rank',
        minWidth: 120,
        flex: 1,
        sortingOrder: ['asc', 'desc', null],
        filter: 'agNumberColumnFilter',
        filterParams: {
            inRangeInclusive: true,
        },
        chartDataType: 'category',
    },
    ...(props.includeOptionalColumns.includes('conceptNames')
        ? [
              {
                  field: 'conceptNames',
                  headerName: 'Product(s)',
                  filter: 'agSetColumnFilter',
                  chartDataType: 'excluded',
              },
          ]
        : []),
    ...(props.includeOptionalColumns.includes('crunchbaseRank') && isArcUser.value
        ? [
              {
                  field: 'crunchbaseRank',
                  headerName: 'Crunchbase Rank',
                  valueFormatter: (p) => p.data?.crunchbaseRank?.toLocaleString() || '--',
                  filter: 'agNumberColumnFilter',
                  chartDataType: 'category',
              },
          ]
        : []),
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('productAlignment.definitionTooltips.innovationScore'),
            headerName: 'Moat Coverage',
        },
        field: 'powerScore',
        headerName: 'Moat Coverage',
        valueGetter: (p) => {
            if (p.data) {
                return parseInt(p.data.powerScore.toFixed(0))
            }
        },
        valueFormatter: (p) => {
            if (p.data) {
                return `${parseInt(p.data.powerScore.toFixed(0)).toLocaleString()}`
            }
        },
        headerTooltip: 'Moat Coverage',
        minWidth: 200,
        flex: 1,
        filter: 'agNumberColumnFilter',
        chartDataType: 'series',
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('productAlignment.definitionTooltips.opportunity'),
            headerName: 'Momentum',
        },
        cellRenderer: 'MTCustomCellScoreChange',
        cellRendererParams: {
            lowerBound: 40,
            upperBound: 50,
            tooltip: {
                increasing: 'Increasing Innovation',
                decreasing: 'Decreasing Innovation',
                steady: 'Steady Innovation',
            },
        },
        field: 'opportunity',
        headerName: 'Momentum',
        headerTooltip: 'Momentum',
        flex: 1,
        minWidth: 150,
        filter: 'agNumberColumnFilter',
        valueGetter: (p) => {
            if (p.data) {
                return Math.round(p.data.opportunity)
            }
        },
        valueFormatter: (p) => {
            if (p.data) {
                return `${Math.round(p.data.opportunity)}`
            }
        },
        chartDataType: 'series',
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('productAlignment.definitionTooltips.percentOfPortfolio'),
            headerName: 'Focus',
        },
        field: 'portfolioFocusPercentage',
        headerName: 'Focus',
        valueGetter: (p) => {
            if (p.data) {
                return parseInt(p.data.portfolioFocusPercentage.toFixed(0))
            }
        },
        valueFormatter: (p) => {
            if (p.data) {
                return `${p.data.portfolioFocusPercentage.toFixed(0)}%`
            }
        },
        headerTooltip: 'Focus',
        flex: 1,
        minWidth: 120,
        filter: 'agNumberColumnFilter',
        chartDataType: 'series',
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('productAlignment.definitionTooltips.marketAppeal'),
            headerName: 'Market Appeal',
        },
        field: 'tpe',
        headerName: 'Market Appeal',
        headerTooltip: 'Market Appeal',
        flex: 1,
        minWidth: 120,
        valueGetter: (p) => {
            if (p.data) {
                return parseInt(p.data.tpe.toFixed(0))
            }
        },
        valueFormatter: (p) => {
            if (p.data) {
                return formatTpeRange(p.data.tpe, p.data.tpeLb, p.data.tpeUb)
            }
        },
        filter: 'agNumberColumnFilter',
        chartDataType: 'series',
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('productAlignment.definitionTooltips.innovationStart'),
            headerName: 'Innovation Start',
        },
        valueGetter: (p) => {
            if (p.data) {
                return filters.toUTCString(p.data.entryPriorityDt) === '1/1/1'
                    ? '---'
                    : new Date(p.data.entryPriorityDt).getFullYear()
            }
        },
        filterParams: {
            inRangeInclusive: true,
        },
        field: 'entryPriorityDt',
        headerName: 'Innovation Start',
        headerTooltip: `Innovation Start`,

        flex: 1,
        minWidth: 200,
        filter: 'agNumberColumnFilter',
        chartDataType: 'excluded',
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('productAlignment.definitionTooltips.fiveYear'),
            headerName: 'Expiring Soon',
        },
        field: 'fiveYearExpirePercent',
        headerName: 'Expiring Soon',
        valueGetter: (p) => {
            if (p.data) {
                return parseInt(p.data.fiveYearExpirePercent.toFixed(0))
            }
        },
        valueFormatter: (p) => {
            if (p.data) {
                return `${p.data.fiveYearExpirePercent.toFixed(0)}%`
            }
        },
        headerTooltip: 'Expiring Soon',
        flex: 1,
        minWidth: 170,
        filter: 'agNumberColumnFilter',
        // chartDataType: 'series'
        chartDataType: 'excluded',
    },
    {
        field: 'hqCountryName',
        headerName: 'HQ Country',
        hide: true,
        filter: 'agSetColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'entityType',
        headerName: 'Ownership Status',
        hide: true,
        filter: 'agSetColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'totalRevenue',
        headerName: 'Revenue',
        hide: true,
        filter: 'agNumberColumnFilter',
        filterParams: {
            filterOptions: [
                'equals',
                'notEqual',
                'lessThan',
                'lessThanOrEqual',
                'greaterThan',
                'greaterThanOrEqual',
                'inRange',
            ],
            defaultOption: 'greaterThan',
        },
        chartDataType: 'excluded',
        valueFormatter: (p) => {
            if (p.data) {
                return filters.abbreviate(p.data.totalRevenue)
            }
        },
    },
    {
        field: 'employeeCount',
        headerName: 'Employee Count',
        hide: true,
        filter: 'agSetColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'avgValidityScore',
        headerName: 'Avg Validity Score',
        hide: true,
        valueGetter: (p) => {
            if (p.data) {
                return parseFloat(p.data.avgValidityScore.toFixed(2))
            }
        },
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.avgValidityScore.toFixed(2)
            }
        },
        filter: 'agNumberColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'priceToEarnings',
        headerName: 'Price to Earnings',
        hide: true,
        filter: 'agNumberColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'yoyRevenueGrowthPct',
        headerName: 'YoY Revenue Growth %',
        hide: true,
        filter: 'agNumberColumnFilter',
    },
    {
        field: 'marketValue',
        headerName: 'Market Cap',
        hide: true,
        filter: 'agNumberColumnFilter',
    },
    {
        field: 'currency',
        headerName: 'Currency',
        hide: true,
        filter: 'agSetColumnFilter',
    },
    {
        field: 'lastFundingRoundInvestmentType',
        headerName: 'Latest Funding Round',
        hide: true,
        valueGetter: (p) => {
            if (p.data) {
                return getInvestmentGroup(p.data.lastFundingRoundInvestmentType)
            }
        },
        filter: 'agSetColumnFilter',
    },
    ...(isProUser.value && props.allowActions
        ? [
              {
                  suppressHeaderMenuButton: true,
                  cellRenderer: 'MTContextMenu',
                  cellRendererParams: {
                      actionsList: [
                          {
                              name: 'View Company Patents',
                              icon: 'fa-square-list',
                              actionName: 'patents',
                              patentsAction: async (params) => {
                                  patentViewerStore.nodeIdList.push(...props.techAreaIds)
                                  patentViewerStore.entityPkList.push(params.data.aonEntityPk)
                                  patentViewerStore.entityName = params.data.aonEntityName
                                  patentViewerStore.listViewType = props.patentListType
                                  await nextTick()
                                  patentViewerStore.showPatentViewerList = true
                              },
                          },
                      ],
                      highlightLabel: true,
                  },
                  pinned: 'right',
                  width: 60,
              },
          ]
        : []),
])

// Quick filter
const shouldApplyQuickFilter = () => {
    return props.quickFilter !== 'all'
}

const applyQuickFilter = (node) => {
    return benchmarkingFilter(node.data, props.quickFilter)
}

const onFilterSortChanged = async () => {
    const filteredRows = []
    if (!gridApi.value) return

    gridApi.value.forEachNodeAfterFilterAndSort((node) => filteredRows.push(node.data))
    emit('filter-change', filteredRows)
    updateRowCount()
}

const clearFilters = () => {
    if (gridApi.value) {
        sidebarClearFilters(gridApi.value)
    }
}
</script>

<style lang="scss" scoped>
.focus-table {
    height: 600px;
}
</style>
